<template>
	<div class="c_modal_container SafetyCheckChecklistModal">
		<div class="c_screen_filter"></div>
		<div class="c_modal_contents">
			<h1 class="title">{{ title }}</h1>
			<div class="c_modal_inner">
				<table class="checklist_table">
					<colgroup>
						<col class="item_col" />
						<col class="answer_col" />
						<col class="answer_col" />
					</colgroup>
					<thead>
						<tr>
							<th colspan="3">{{ headerTitle }}</th>
						</tr>
					</thead>
					<tbody v-for="section in checklistSections" :key="section.title">
						<tr class="section_row">
							<td>{{ section.title }}</td>
							<td>Y</td>
							<td>N</td>
						</tr>
						<tr v-for="item in section.items" :key="item.id">
							<td>{{ item.label }}</td>
							<td>
								<label class="radio_marker">
									<input v-model="answers[item.id]" type="radio" :name="item.id" value="Y" />
									<span></span>
								</label>
							</td>
							<td>
								<label class="radio_marker">
									<input v-model="answers[item.id]" type="radio" :name="item.id" value="N" />
									<span></span>
								</label>
							</td>
						</tr>
					</tbody>
				</table>
				<div v-if="checklistSections.length === 0" class="empty_checklist">조회된 항목이 없습니다.</div>
			</div>

			<div class="modal_bottom">
				<div class="btn_wrap">
					<button v-if="secondaryButtonLabel" type="button" class="btn_cancel" @click="onClickClose">
						{{ secondaryButtonLabel }}
					</button>
					<button type="button" class="btn_save" @click="onClickPrimary">{{ primaryButtonLabel }}</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { defineEmits, defineProps, onMounted, reactive, ref } from 'vue';
	import { useStore } from 'vuex';
	import { getEngChecklistPop, getUserInfo, saveEngCheckVanResult } from '@/api/user';

	const props = defineProps({
		title: {
			type: String,
			default: '차량안전점검',
		},
		headerTitle: {
			type: String,
			default: '점검 항목',
		},
		checkCode: {
			type: String,
			default: 'VEHICLE',
			validator: (value) => ['VEHICLE', 'WORK_START'].includes(value),
		},
		primaryButtonLabel: {
			type: String,
			default: '저장',
		},
		secondaryButtonLabel: {
			type: String,
			default: '',
		},
		modalName: {
			type: String,
			default: 'SafetyCheckChecklistModal',
		},
		closeOnPrimary: {
			type: Boolean,
			default: true,
		},
		receiptPk: {
			type: [Number, String],
			default: null,
		},
		successMessage: {
			type: String,
			default: '',
		},
	});
	const emit = defineEmits(['primary']);
	const store = useStore();

	const answers = reactive({});
	const checklistSections = ref([]);

	const getChecklistItems = (response) => {
		return response?.check_van || response?.check_work_start || response?.check_start || response?.check_tbm || response?.checklist || [];
	};

	const setChecklistSections = (response) => {
		const checklistItems = getChecklistItems(response);
		if (!Array.isArray(checklistItems) || checklistItems.length === 0) return;

		checklistSections.value = [
			{
				title: props.title,
				items: [...checklistItems]
					.sort((a, b) => (a.sort_order || a.seq_no || 0) - (b.sort_order || b.seq_no || 0))
					.map((item, index) => ({
						id: item.check_van_pk || item.check_pk || item.seq_no || index,
						checkVanPk: item.check_van_pk || item.check_pk,
						label: item.content,
						correctAnswer: item.correct_answer,
					})),
			},
		];
	};

	const getSavePayload = () => {
		return {
			check_code: props.checkCode,
			receipt_pk: props.receiptPk,
			items: checklistSections.value.flatMap((section) =>
				section.items.map((item) => ({
					check_van_pk: item.checkVanPk || item.id,
					selected_answer: answers[item.id],
				})),
			),
		};
	};

	const hasInvalidAnswer = () => {
		return checklistSections.value.some((section) =>
			section.items.some((item) => {
				const selectedAnswer = answers[item.id];
				const correctAnswer = item.correctAnswer;

				if (!selectedAnswer) return true;
				if (!correctAnswer || correctAnswer === 'X') return false;
				return selectedAnswer !== correctAnswer;
			}),
		);
	};

	const onClickClose = () => {
		store.commit('modal/closeModal', props.modalName);
	};

	const onClickPrimary = async () => {
		if (hasInvalidAnswer()) {
			alert('체크가 안되거나 정답이 아닌 리스트가 있습니다. 확인 부탁드립니다.');
			return;
		}

		try {
			const response = await saveEngCheckVanResult(getSavePayload());
			console.log('saveEngCheckVanResult response:', response);
			const userInfoResponse = await getUserInfo();
			store.commit('user/setUserInfo', userInfoResponse);
			if (props.successMessage) {
				alert(props.successMessage);
			}
			emit('primary');
			if (props.closeOnPrimary) {
				store.commit('modal/closeModal', props.modalName);
			}
		} catch (error) {
			console.log('saveEngCheckVanResult error:', error);
			alert('저장 중 오류가 발생했습니다.');
		}
	};

	onMounted(async () => {
		try {
			const response = await getEngChecklistPop({ check_code: props.checkCode });
			console.log('getEngChecklistPop response:', response);
			setChecklistSections(response);
		} catch (error) {
			console.log('getEngChecklistPop error:', error);
		}
	});
</script>

<style scoped src="@/assets/css/components/modal.css"></style>
<style scoped>
	.SafetyCheckChecklistModal {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.6rem;
	}

	.SafetyCheckChecklistModal .c_modal_contents {
		position: relative;
		top: auto;
		bottom: auto;
		left: auto;
		transform: none;
		width: 100%;
		max-width: 40rem;
		height: 84vh;
		max-height: 84vh;
		padding: 0;
		overflow: hidden;
		border-radius: 0.8rem;
		display: flex;
		flex-direction: column;
	}

	.SafetyCheckChecklistModal .title {
		height: 5.6rem;
		border-bottom: 0.2rem solid #e23b4a;
		font-size: var(--font-size-mid);
		font-weight: var(--font-w-bold);
		line-height: 5.6rem;
		text-align: center;
	}

	.SafetyCheckChecklistModal .c_modal_inner {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
	}

	.checklist_table {
		width: 100%;
		border-collapse: collapse;
		table-layout: fixed;
	}

	.item_col {
		width: auto;
	}

	.answer_col {
		width: 5.6rem;
	}

	.checklist_table th,
	.checklist_table td {
		border-right: 1px solid #e5e7eb;
		border-bottom: 1px solid #e5e7eb;
		color: #333;
		font-size: var(--font-size-small);
	}

	.checklist_table th:last-child,
	.checklist_table td:last-child {
		border-right: 0;
	}

	.checklist_table thead th {
		height: 4rem;
		background-color: #8d8f96;
		color: #fff;
		font-weight: var(--font-w-bold);
		text-align: center;
	}

	.checklist_table thead th:first-child {
		text-align: center;
	}

	.checklist_table th:first-child,
	.checklist_table td:first-child {
		width: auto;
		padding: 1.1rem 1rem;
		line-height: 1.45;
		text-align: left;
		word-break: keep-all;
	}

	.checklist_table th:not(:first-child),
	.checklist_table td:not(:first-child) {
		text-align: center;
		vertical-align: middle;
	}

	.section_row td {
		height: 3.8rem;
		background-color: #eeeeef;
		font-weight: var(--font-w-bold);
	}

	.radio_marker {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		min-height: 4.4rem;
		cursor: pointer;
	}

	.radio_marker input {
		display: none;
	}

	.radio_marker span {
		display: block;
		width: 2.2rem;
		height: 2.2rem;
		border: 0.2rem solid #e5e7eb;
		border-radius: 0.5rem;
		background-color: #f7f7f9;
	}

	.radio_marker input:checked + span {
		border-color: #777892;
		background-color: #777892;
		box-shadow: inset 0 0 0 0.4rem #fff;
	}

	.empty_checklist {
		padding: 2.4rem 1.4rem;
		border-bottom: 1px solid #e5e7eb;
		color: #666;
		font-size: var(--font-size-small);
		text-align: center;
	}

	.modal_bottom {
		flex: 0 0 auto;
		padding: 1.2rem 1.4rem calc(1.6rem + env(safe-area-inset-bottom));
		background-color: #fff;
	}

	.modal_bottom .btn_wrap {
		display: flex;
		gap: 0.8rem;
	}

	.btn_cancel,
	.btn_save {
		width: 100%;
		height: 5.2rem;
		border-radius: 0.8rem;
		font-size: var(--font-size-mid);
		font-weight: var(--font-w-bold);
	}

	.btn_cancel {
		border: 1px solid #d8dce3;
		background-color: #fff;
		color: #333;
	}

	.btn_save {
		background-color: #777892;
		color: #fff;
	}
</style>
